import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

// Status tags use a quiet neutral chip; the status color is carried by a small
// leading dot (Linear / Vercel style). Pass `hideDot` for non-status labels.
const badgeVariants = cva(
  // `leading-tight` gives Poppins' ascenders headroom so the tops of glyphs
  // aren't shaved in the chip (line-height:1 / leading-none rounded the cap
  // clearance away at dpr 1). text-xs precedes it so tailwind-merge keeps it.
  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium leading-tight transition-colors focus:outline-none focus:ring-2 focus:ring-ring/35 focus:ring-offset-2 focus:ring-offset-background",
  {
    variants: {
      variant: {
        default: "bg-muted/70 text-foreground/80 ring-1 ring-inset ring-border/50",
        info: "bg-muted/70 text-foreground/80 ring-1 ring-inset ring-border/50",
        secondary: "bg-muted/70 text-foreground/80 ring-1 ring-inset ring-border/50",
        destructive: "bg-muted/70 text-foreground/80 ring-1 ring-inset ring-border/50",
        success: "bg-muted/70 text-foreground/80 ring-1 ring-inset ring-border/50",
        warning: "bg-muted/70 text-foreground/80 ring-1 ring-inset ring-border/50",
        gold: "bg-muted/70 text-foreground/80 ring-1 ring-inset ring-border/50",
        premium: "bg-muted/70 text-foreground/80 ring-1 ring-inset ring-border/50",
        outline: "border border-[#ececec] dark:border-[#ffffff1a]/80 bg-card/50 text-muted-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const DOT_COLOR: Record<string, string> = {
  default: "bg-primary",
  info: "bg-primary",
  success: "bg-success",
  warning: "bg-warning",
  destructive: "bg-destructive",
  gold: "bg-brand-gold",
  premium: "bg-brand-gold",
  secondary: "bg-muted-foreground/55",
}

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /** Hide the leading status dot — use for decorative / non-status labels. */
  hideDot?: boolean
}

function Badge({ className, variant, hideDot, children, ...props }: BadgeProps) {
  const v = variant ?? "default"
  const dot = !hideDot && v !== "outline" ? DOT_COLOR[v] : undefined
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props}>
      {dot && <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", dot)} aria-hidden="true" />}
      {children}
    </div>
  )
}

export { Badge, badgeVariants }
